import Navbar from "../Navbar"
import Footer from "../Footer"
import { motion, useScroll, useTransform } from "framer-motion";
import dryBoxService from "../../../static/images/services/dry_box_service.png";

const PrePostRepairSurvey = () => {
  const fadeUp = {
    hidden: { opacity: 0, y: 60 },
    show: { opacity: 1, y: 0 },
  };
  const features = [
    {
      title: "Damage Identification",
      description: "Expert identification and documentation of all existing damage before repairs begin.",
    },
    {
      title: "Repair Scope Determination",
      description: "Professional assessment to determine scope and extent of required repairs.",
    },
    {
      title: "Pre-Repair Documentation",
      description: "Comprehensive baseline documentation before any repair work commences.",
    },
    {
      title: "Quality Verification",
      description: "Post-repair inspection ensuring all repairs meet quality standards and specifications.",
    },
    {
      title: "Cost Estimation Support",
      description: "Expert assessment to support repair cost estimation and budget planning.",
    },
    {
      title: "Warranty Compliance",
      description: "Verification that repairs meet warranty requirements and industry standards.",
    },
  ]

  const benefits = [
    "Accurate damage assessment",
    "Cost control",
    "Quality assurance",
    "Dispute prevention",
    "Comprehensive documentation",
    "Professional standards",
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[60vh] mt-20 overflow-hidden">
        <img
          src={dryBoxService}
          alt="Pre/Post Repair Survey"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-black-800/90 to-transparent">
          <div className="container mx-auto px-4 h-full flex items-center">
            <div className="max-w-3xl text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Pre/Post Repair Survey</h1>
              <p className="text-xl text-blue-100">
                Comprehensive surveys for dry van, reefer, and tank containers before and after repairs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Overview */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-semibold text-orange mb-6">Service Overview</h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Our pre and post repair survey services provide comprehensive documentation of container condition
              throughout the repair process. Whether for dry van, reefer, or tank containers, we offer expert assessment
              and professional reporting that ensures quality repairs and protects all stakeholders.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Pre-repair surveys establish a clear baseline of damage and required work, while post-repair surveys
              verify that repairs meet specifications and quality standards. Our detailed documentation supports cost
              management, quality assurance, and warranty compliance for all repair work.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-6 text-center">Key Features</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Why Choose Our Service</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-orange mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-lg text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Supported Container Types */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-gray-50 pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Container Types We Service</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { title: "Dry Van", desc: "Standard dry containers for general cargo" },
              { title: "Reefer Containers", desc: "Temperature-controlled containers for perishables" },
              { title: "Tank Containers", desc: "Specialized containers for liquid cargo" },
            ].map((type, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl text-center">
                <h3 className="text-xl font-semibold text-gray-700 mb-3">{type.title}</h3>
                <p className="text-gray-600">{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Process Section */}
      <motion.section
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="py-20 bg-white pt-14">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-semibold text-orange mb-12 text-center">Our Process</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mx-auto">
              {[
                {
                  num: "1",
                  title: "Pre-Repair Inspection",
                  desc: "Document all damage and establish baseline for repair planning",
                },
                {
                  num: "2",
                  title: "Assessment Report",
                  desc: "Detailed report with damage identification and repair recommendations",
                },
                {
                  num: "3",
                  title: "Repair Oversight",
                  desc: "Optional inspection during repair process to ensure quality",
                },
                {
                  num: "4",
                  title: "Post-Repair Inspection",
                  desc: "Comprehensive verification that all repairs meet specifications",
                },
                {
                  num: "5",
                  title: "Final Documentation",
                  desc: "Complete report certifying repair quality and container condition",
                },
              ].map((step, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-lg bg-orange text-white font-semibold">
                      {step.num}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section
        id="contact"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
      >
        <Footer />
      </motion.section>
    </div>
  )
}

export default PrePostRepairSurvey;